import React, { useState } from 'react';
import { ChevronLeft, Building, Mail, Phone, MapPin, Users, TrendingUp, Award, Star, Calendar, CheckCircle, Clock, AlertCircle, XCircle, Send, MessageCircle } from 'lucide-react';
interface BrokerageProfilePageProps {
  brokerageId: number;
  brokerageName: string;
  isInNetwork: boolean; // true = My Brokerage, false = Suggested Brokerage
  onBack: () => void;
  onAddToNetwork?: () => void;
  onRMIClick?: (rmiId: number) => void;
}
interface RMIRequest {
  id: number;
  code: string;
  projectName: string;
  propertyType: string;
  clientName: string;
  requestDate: string;
  status: 'pending' | 'in-progress' | 'approved' | 'rejected';
  priority: 'high' | 'medium' | 'low';
}
export function BrokerageProfilePage({
  brokerageId,
  brokerageName,
  isInNetwork,
  onBack,
  onAddToNetwork,
  onRMIClick
}: BrokerageProfilePageProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'rmi'>('overview');
  const [showAccreditationModal, setShowAccreditationModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'gold' | 'silver' | 'bronze'>('silver');
  const [accreditationMessage, setAccreditationMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Brokerage logos mapping
  const brokerageLogos: {
    [key: string]: string;
  } = {
    'Real Estate Investment Group': 'https://cdn.magicpatterns.com/uploads/6BgvouriBk6paAGWqLe7NS/OIP.1Lbk_6fxHaaU9UwfINN2JAAAAA.webp',
    'One Broker Group': 'https://cdn.magicpatterns.com/uploads/jZaBreBdfYsDWTGSDxyK6a/OIP.FOaAYWJH6ikQ-svnuEwxwwHaEo.webp',
    'Alliance Brokers Realty': 'https://cdn.magicpatterns.com/uploads/niPHF2enxAVm3q3inB94mf/OIP.KgNwAD0NUiClwWnlKKGHXAAAAA.webp',
    'RGM Commercial Real Estate': 'https://cdn.magicpatterns.com/uploads/2m6PX3t2LTb47QV7aD5ZWH/OIP.LKSfM12Q4CXDf4ucVM1RbgHaHa.webp',
    'MG Real Estate & Investments': 'https://cdn.magicpatterns.com/uploads/cNZRbMg6yCRYutpQ9xSnL9/OIP.LsnAiW6moxtg5fFjG12K5AHaHa.webp'
  };
  // Get logo for this brokerage, fallback to first logo if not found
  const brokerageLogo = brokerageLogos[brokerageName] || 'https://cdn.magicpatterns.com/uploads/6BgvouriBk6paAGWqLe7NS/OIP.1Lbk_6fxHaaU9UwfINN2JAAAAA.webp';
  // Mock brokerage data
  const brokerage = {
    id: brokerageId,
    name: brokerageName,
    logo: brokerageLogo,
    email: 'partnerships@eliteproperties.com',
    phone: '+20 2 123 456 7890',
    website: 'www.eliteproperties.com',
    establishedYear: 2010,
    agentsCount: 45,
    dealsCompleted: 230,
    rating: 4.9,
    specialization: 'Luxury & Commercial Properties',
    officeLocations: ['New Cairo', 'Sheikh Zayed', '6th of October'],
    memberships: {
      gold: 5,
      silver: 3,
      bronze: 2
    },
    totalMemberships: 10,
    description: 'Elite Properties Group is a leading real estate brokerage in Egypt, specializing in luxury residential and commercial properties. With over 45 certified agents and a track record of 230+ successful deals, we provide comprehensive real estate solutions.',
    services: ['Property Sales & Leasing', 'Investment Consulting', 'Property Management', 'Market Analysis', 'Legal Support'],
    certifications: ['Egyptian Real Estate Association Member', 'ISO 9001:2015 Certified', 'Licensed by Ministry of Housing']
  };
  // Mock RMI requests (only for My Brokerages)
  const [rmiRequests] = useState<RMIRequest[]>([{
    id: 1,
    code: 'RMI-2024-001',
    projectName: 'Mountain View iCity',
    propertyType: '3BR Apartment',
    clientName: 'Ahmed Hassan',
    requestDate: '2 hours ago',
    status: 'pending',
    priority: 'high'
  }, {
    id: 2,
    code: 'RMI-2024-002',
    projectName: 'Hyde Park New Cairo',
    propertyType: 'Villa 250sqm',
    clientName: 'Sarah Mohamed',
    requestDate: '5 hours ago',
    status: 'in-progress',
    priority: 'medium'
  }, {
    id: 3,
    code: 'RMI-2024-003',
    projectName: 'Palm Hills Badya',
    propertyType: 'Townhouse',
    clientName: 'Mohamed Ali',
    requestDate: '1 day ago',
    status: 'approved',
    priority: 'high'
  }, {
    id: 4,
    code: 'RMI-2024-004',
    projectName: 'Madinaty',
    propertyType: '2BR Apartment',
    clientName: 'Nadia Hassan',
    requestDate: '2 days ago',
    status: 'rejected',
    priority: 'low'
  }]);
  const handleSendAccreditationRequest = () => {
    if (!accreditationMessage.trim()) {
      alert('Please enter a message for your accreditation request');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowAccreditationModal(false);
      alert(`Accreditation request sent to ${brokerageName}!`);
      if (onAddToNetwork) {
        onAddToNetwork();
      }
    }, 1500);
  };
  const getStatusConfig = (status: RMIRequest['status']) => {
    const configs = {
      pending: {
        label: 'Pending',
        icon: Clock,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200'
      },
      'in-progress': {
        label: 'In Progress',
        icon: AlertCircle,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      },
      approved: {
        label: 'Approved',
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      },
      rejected: {
        label: 'Rejected',
        icon: XCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      }
    };
    return configs[status];
  };
  const getPriorityColor = (priority: RMIRequest['priority']) => {
    const colors = {
      high: 'bg-red-500',
      medium: 'bg-yellow-500',
      low: 'bg-green-500'
    };
    return colors[priority];
  };
  const getTierBadgeColor = (tier: 'gold' | 'silver' | 'bronze') => {
    switch (tier) {
      case 'gold':
        return 'border-yellow-400 bg-yellow-50';
      case 'silver':
        return 'border-gray-400 bg-gray-50';
      case 'bronze':
        return 'border-orange-600 bg-orange-50';
    }
  };
  const getTierTextColor = (tier: 'gold' | 'silver' | 'bronze') => {
    switch (tier) {
      case 'gold':
        return 'text-yellow-700';
      case 'silver':
        return 'text-gray-700';
      case 'bronze':
        return 'text-orange-700';
    }
  };
  const getMembershipBadges = () => {
    return <div className="flex items-center gap-2 flex-wrap" data-id="element-129">
        {brokerage.memberships.gold > 0 && <div className="flex items-center gap-1 bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full text-sm font-bold" data-id="element-130">
            <Award className="w-4 h-4" data-id="element-131" />
            {brokerage.memberships.gold} Gold
          </div>}
        {brokerage.memberships.silver > 0 && <div className="flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-bold" data-id="element-132">
            <Award className="w-4 h-4" data-id="element-133" />
            {brokerage.memberships.silver} Silver
          </div>}
        {brokerage.memberships.bronze > 0 && <div className="flex items-center gap-1 bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-sm font-bold" data-id="element-134">
            <Award className="w-4 h-4" data-id="element-135" />
            {brokerage.memberships.bronze} Bronze
          </div>}
      </div>;
  };
  return <div className="min-h-screen bg-neutral-bg pb-6 font-sans w-full" data-id="element-136">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-137">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark px-4 pt-8 pb-20 relative overflow-hidden" data-id="element-138">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" data-id="element-139"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-10 -mb-10 blur-xl" data-id="element-140"></div>

          <div className="relative z-10" data-id="element-141">
            <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors mb-6" data-id="element-142">
              <ChevronLeft className="w-6 h-6" data-id="element-143" />
            </button>

            {/* Brokerage Header */}
            <div className="flex items-start gap-4" data-id="element-144">
              <div className="w-20 h-20 rounded-2xl bg-white shadow-xl flex items-center justify-center flex-shrink-0 p-3" data-id="element-145">
                <img src={brokerage.logo} alt={brokerage.name} className="w-full h-full object-contain" data-id="element-146" />
              </div>
              <div className="flex-1" data-id="element-147">
                <h1 className="text-2xl font-bold text-white mb-2" data-id="element-148">
                  {brokerage.name}
                </h1>
                <p className="text-white/80 text-sm mb-3" data-id="element-149">
                  {brokerage.specialization}
                </p>
                <div className="flex items-center gap-2" data-id="element-150">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.floor(brokerage.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-white/30'}`} data-id="element-151" />)}
                  <span className="text-white/90 text-sm ml-1" data-id="element-152">
                    ({brokerage.rating})
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="px-4 -mt-12 relative z-20 mb-6" data-id="element-153">
          <div className="grid grid-cols-3 gap-3" data-id="element-154">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center" data-id="element-155">
              <Users className="w-6 h-6 text-primary mx-auto mb-2" data-id="element-156" />
              <p className="text-2xl font-bold text-neutral-text mb-1" data-id="element-157">
                {brokerage.agentsCount}
              </p>
              <p className="text-xs text-gray-500" data-id="element-158">Agents</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center" data-id="element-159">
              <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-2" data-id="element-160" />
              <p className="text-2xl font-bold text-neutral-text mb-1" data-id="element-161">
                {brokerage.dealsCompleted}
              </p>
              <p className="text-xs text-gray-500" data-id="element-162">Deals</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center" data-id="element-163">
              <Award className="w-6 h-6 text-yellow-500 mx-auto mb-2" data-id="element-164" />
              <p className="text-2xl font-bold text-neutral-text mb-1" data-id="element-165">
                {brokerage.totalMemberships}
              </p>
              <p className="text-xs text-gray-500" data-id="element-166">Partnerships</p>
            </div>
          </div>
        </div>

        {/* Send Accreditation Request Button (for Suggested Brokerages) */}
        {!isInNetwork && <div className="px-4 mb-6" data-id="element-167">
            <button onClick={() => setShowAccreditationModal(true)} className="w-full bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-dark hover:to-secondary text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-secondary/30 flex items-center justify-center gap-2" data-id="element-168">
              <Send className="w-5 h-5" data-id="element-169" />
              Send Accreditation Request
            </button>
          </div>}

        {/* Tabs (only for My Brokerages) */}
        {isInNetwork && <div className="px-4 mb-6" data-id="element-170">
            <div className="bg-white rounded-xl p-1 shadow-sm border border-gray-100 flex gap-1" data-id="element-171">
              <button onClick={() => setActiveTab('overview')} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'overview' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-172">
                Overview
              </button>
              <button onClick={() => setActiveTab('rmi')} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'rmi' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-173">
                RMI Requests
              </button>
            </div>
          </div>}

        {/* Content */}
        {activeTab === 'overview' && <div className="px-4 space-y-4" data-id="element-174">
            {/* About */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-175">
              <h2 className="text-base font-bold text-neutral-text mb-3" data-id="element-176">
                About
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-177">
                {brokerage.description}
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-178">
                <Calendar className="w-4 h-4" data-id="element-179" />
                <span data-id="element-180">Established {brokerage.establishedYear}</span>
              </div>
            </section>

            {/* Contact Information */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-181">
              <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-182">
                Contact Information
              </h2>
              <div className="space-y-3" data-id="element-183">
                <div className="flex items-center gap-3" data-id="element-184">
                  <Mail className="w-5 h-5 text-gray-400" data-id="element-185" />
                  <span className="text-sm text-gray-700" data-id="element-186">
                    {brokerage.email}
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-187">
                  <Phone className="w-5 h-5 text-gray-400" data-id="element-188" />
                  <span className="text-sm text-gray-700" data-id="element-189">
                    {brokerage.phone}
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-190">
                  <Building className="w-5 h-5 text-gray-400" data-id="element-191" />
                  <span className="text-sm text-gray-700" data-id="element-192">
                    {brokerage.website}
                  </span>
                </div>
              </div>
            </section>

            {/* Office Locations */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-193">
              <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-194">
                Office Locations
              </h2>
              <div className="space-y-2" data-id="element-195">
                {brokerage.officeLocations.map((location, idx) => <div key={idx} className="flex items-center gap-3" data-id="element-196">
                    <MapPin className="w-4 h-4 text-primary" data-id="element-197" />
                    <span className="text-sm text-gray-700" data-id="element-198">{location}</span>
                  </div>)}
              </div>
            </section>

            {/* Partnership Tiers */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-199">
              <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-200">
                Partnership Tiers with Other Developers
              </h2>
              {getMembershipBadges()}
            </section>

            {/* Services */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-201">
              <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-202">
                Services Offered
              </h2>
              <div className="space-y-2" data-id="element-203">
                {brokerage.services.map((service, idx) => <div key={idx} className="flex items-center gap-3" data-id="element-204">
                    <CheckCircle className="w-4 h-4 text-green-500" data-id="element-205" />
                    <span className="text-sm text-gray-700" data-id="element-206">{service}</span>
                  </div>)}
              </div>
            </section>

            {/* Certifications */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-207">
              <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-208">
                Certifications & Licenses
              </h2>
              <div className="space-y-2" data-id="element-209">
                {brokerage.certifications.map((cert, idx) => <div key={idx} className="flex items-center gap-3" data-id="element-210">
                    <Award className="w-4 h-4 text-primary" data-id="element-211" />
                    <span className="text-sm text-gray-700" data-id="element-212">{cert}</span>
                  </div>)}
              </div>
            </section>
          </div>}

        {/* RMI Requests Tab (only for My Brokerages) */}
        {activeTab === 'rmi' && isInNetwork && <div className="px-4 space-y-3" data-id="element-213">
            {rmiRequests.length > 0 ? rmiRequests.map(rmi => {
          const statusConfig = getStatusConfig(rmi.status);
          const StatusIcon = statusConfig.icon;
          return <button key={rmi.id} onClick={() => onRMIClick && onRMIClick(rmi.id)} className={`w-full bg-white rounded-xl border-2 ${statusConfig.borderColor} p-4 shadow-sm hover:shadow-md transition-shadow text-left`} data-id="element-214">
                    <div className="flex items-start justify-between mb-3" data-id="element-215">
                      <div className="flex items-center gap-2" data-id="element-216">
                        <span className="text-xs font-bold text-secondary" data-id="element-217">
                          {rmi.code}
                        </span>
                        <div className={`w-2 h-2 rounded-full ${getPriorityColor(rmi.priority)}`} data-id="element-218" />
                      </div>
                      <span className={`flex items-center gap-1 ${statusConfig.bgColor} ${statusConfig.color} text-xs font-semibold px-2 py-1 rounded-full`} data-id="element-219">
                        <StatusIcon className="w-3 h-3" data-id="element-220" />
                        {statusConfig.label}
                      </span>
                    </div>

                    <h4 className="font-bold text-neutral-text text-sm mb-1" data-id="element-221">
                      {rmi.projectName}
                    </h4>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-222">
                      {rmi.propertyType}
                    </p>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-223">
                      <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-224">
                        <MessageCircle className="w-3.5 h-3.5" data-id="element-225" />
                        <span data-id="element-226">{rmi.clientName}</span>
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-227">
                        {rmi.requestDate}
                      </span>
                    </div>
                  </button>;
        }) : <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-100" data-id="element-228">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-229" />
                <h3 className="text-base font-semibold text-neutral-text mb-2" data-id="element-230">
                  No RMI Requests
                </h3>
                <p className="text-sm text-gray-500" data-id="element-231">
                  No requests from this brokerage yet
                </p>
              </div>}
          </div>}

        {/* Accreditation Request Modal */}
        {showAccreditationModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-232">
            <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[90vh]" data-id="element-233">
              {/* Header */}
              <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl" data-id="element-234">
                <div className="flex items-center gap-3" data-id="element-235">
                  <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-secondary" data-id="element-236">
                    <Send className="w-5 h-5" data-id="element-237" />
                  </div>
                  <h2 className="text-xl font-bold text-neutral-text" data-id="element-238">
                    Send Accreditation Request
                  </h2>
                </div>
                <button onClick={() => setShowAccreditationModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-239">
                  <XCircle className="w-5 h-5" data-id="element-240" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-241">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3" data-id="element-242">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-243" />
                  <div data-id="element-244">
                    <p className="text-sm font-semibold text-blue-900 mb-1" data-id="element-245">
                      Partnership Request
                    </p>
                    <p className="text-xs text-blue-700" data-id="element-246">
                      Send an accreditation request to {brokerageName} to
                      establish a partnership and start collaborating on deals.
                    </p>
                  </div>
                </div>

                {/* Partnership Tier Selection */}
                <div data-id="element-247">
                  <label className="block text-sm font-bold text-neutral-text mb-3" data-id="element-248">
                    Select Partnership Tier *
                  </label>
                  <div className="grid grid-cols-3 gap-3" data-id="element-249">
                    <button onClick={() => setSelectedTier('bronze')} className={`p-4 rounded-xl border-2 transition-all ${selectedTier === 'bronze' ? 'border-orange-600 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-250">
                      <Award className={`w-8 h-8 mx-auto mb-2 ${selectedTier === 'bronze' ? 'text-orange-600' : 'text-gray-400'}`} data-id="element-251" />
                      <p className={`text-sm font-bold ${selectedTier === 'bronze' ? 'text-orange-700' : 'text-gray-600'}`} data-id="element-252">
                        Bronze
                      </p>
                    </button>

                    <button onClick={() => setSelectedTier('silver')} className={`p-4 rounded-xl border-2 transition-all ${selectedTier === 'silver' ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-253">
                      <Award className={`w-8 h-8 mx-auto mb-2 ${selectedTier === 'silver' ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-254" />
                      <p className={`text-sm font-bold ${selectedTier === 'silver' ? 'text-gray-700' : 'text-gray-600'}`} data-id="element-255">
                        Silver
                      </p>
                    </button>

                    <button onClick={() => setSelectedTier('gold')} className={`p-4 rounded-xl border-2 transition-all ${selectedTier === 'gold' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-256">
                      <Award className={`w-8 h-8 mx-auto mb-2 ${selectedTier === 'gold' ? 'text-yellow-600' : 'text-gray-400'}`} data-id="element-257" />
                      <p className={`text-sm font-bold ${selectedTier === 'gold' ? 'text-yellow-700' : 'text-gray-600'}`} data-id="element-258">
                        Gold
                      </p>
                    </button>
                  </div>
                </div>

                {/* Message */}
                <div data-id="element-259">
                  <label className="block text-sm font-bold text-neutral-text mb-2" data-id="element-260">
                    Message to Brokerage *
                  </label>
                  <textarea value={accreditationMessage} onChange={e => setAccreditationMessage(e.target.value)} placeholder="Introduce your company and explain why you'd like to partner with this brokerage..." rows={6} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary resize-none text-sm" data-id="element-261" />
                  <p className="text-xs text-gray-400 mt-2" data-id="element-262">
                    {accreditationMessage.length} / 500 characters
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 p-6 border-t border-gray-100" data-id="element-263">
                <div className="flex gap-3" data-id="element-264">
                  <button onClick={() => setShowAccreditationModal(false)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition-colors" data-id="element-265">
                    Cancel
                  </button>
                  <button onClick={handleSendAccreditationRequest} disabled={!accreditationMessage.trim() || isSubmitting} className="flex-1 bg-secondary hover:bg-secondary-dark text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-266">
                    {isSubmitting ? <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-267" />
                        Sending...
                      </> : <>
                        <Send className="w-4 h-4" data-id="element-268" />
                        Send Request
                      </>}
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}